import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { SelectField, Label, Input, TooltipV2, BaseModalV2 } from '../../components/common';
import { InfoIcon } from '../../components/common/Icons';
import { PrimaryButton, SecondaryButton } from '../../components/common/ButtonsV2';
import { getFacilitiesByOrganization } from '../../services/apiServices';
import { NO_FACILITIES_AVAILABLE, EMPTY_FIELD_ERROR } from '../../constants/errorMessages';

/**
 * A form component for adding or editing a beacon.
 * It includes fields for beacon identifier, organization, facility, and location name.
 *
 * @param {Object} props - The properties passed to the component.
 * @param {Function} props.onSubmit - Callback function to handle form submission.
 * @param {Function} props.onClose - Callback function to handle modal close.
 * @param {Array} props.organizations - List of organizations for the dropdown.
 * @param {Boolean} props.isUserAdmin - Flag indicating if the user is an admin.
 * @param {String} props.userOrganization - The user's organization if not an admin.
 * @param {Object} props.initialData - Initial data for pre-filling the form (optional).
 * @returns {JSX.Element} - The rendered form component.
 */
const BeaconForm = ({
  onSubmit,
  onClose,
  organizations,
  isUserAdmin,
  userOrganization,
  initialData,
}) => {
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isValid },
  } = useForm({ defaultValues: initialData || {} });

  const [facilities, setFacilities] = useState([]);
  const [noFacilityAvailableError, setNoFacilityAvailableError] = useState('');
  const selectedOrganization = watch('organization_id');

  /**
   * Effect hook that sets initial form data if available.
   * It updates the form fields using the `setValue` method from react-hook-form.
   */
  useEffect(() => {
    if (initialData) {
      Object.keys(initialData).forEach((key) => {
        setValue(key, initialData[key]);
      });
    }
  }, [initialData, setValue, facilities]);

  /**
   * Effect hook to fetch facilities for the selected organization.
   * If the user is an admin, it fetches facilities for the selected organization.
   * If not, it uses the user's organization.
   */
  useEffect(() => {
    const fetchFacilities = async () => {
      try {
        if (isUserAdmin && !selectedOrganization) {
          setFacilities([]);
          return;
        }

        const organizationId = isUserAdmin ? selectedOrganization : userOrganization;
        const data = await getFacilitiesByOrganization(organizationId);
        setFacilities(data);
        setNoFacilityAvailableError(!data?.length ? NO_FACILITIES_AVAILABLE : '');
      } catch (error) {
        const errorMessage =
          error.response && error.response.status === 404
            ? NO_FACILITIES_AVAILABLE
            : 'Error fetching facilities. Please try again later.';

        setNoFacilityAvailableError(errorMessage);
      }
    };
    fetchFacilities();
  }, [selectedOrganization, isUserAdmin, userOrganization]);

  const disableFacilities = !facilities.length;

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="text-black">
      <div>
        <div className="mb-[25px]">
          <Label
            customClass="text-[14px] leading-[16px] mb-[8px]"
            htmlFor="beaconIdentifier"
            isRequired
          >
            Beacon Identifier
          </Label>
          <Input
            {...register('beaconIdentifier', { required: EMPTY_FIELD_ERROR })}
            variant="tertiary"
            id="beaconIdentifier"
            name="beaconIdentifier"
            customClass="h-[56px]"
            placeholder="e.g., AA:BB:CC:DD:EE:FF"
          />
          {errors.beaconIdentifier && (
            <span className="text-red-500 text-[12px]">{errors.beaconIdentifier.message}</span>
          )}
        </div>

        {isUserAdmin && (
          <div className="mb-[25px]">
            <Label
              customClass="text-[14px] leading-[16px] mb-[8px]"
              htmlFor="organization_id"
              isRequired
            >
              Organization
            </Label>
            <SelectField
              label="Organization"
              name="organization_id"
              options={organizations}
              register={register}
              errors={errors}
              required={true}
            />
            {errors.organization_id && (
              <span className="text-red-500 text-[12px]">{errors.organization_id.message}</span>
            )}
          </div>
        )}

        <div className="mb-[25px]">
          <Label customClass="text-[14px] leading-[16px] mb-[8px]" htmlFor="facility" isRequired>
            Facility
          </Label>
          <SelectField
            label="Facility"
            name="facility"
            options={facilities}
            register={register}
            errors={errors}
            required={true}
            disabled={disableFacilities}
          />
          {errors.facility && (
            <span className="text-red-500 text-[12px]">{errors.facility.message}</span>
          )}
          {noFacilityAvailableError && (
            <span className="text-red-500 text-[12px]">{noFacilityAvailableError}</span>
          )}
        </div>

        <div className="mb-[25px]">
          <Label
            customClass="text-[14px] leading-[16px] mb-[8px]"
            htmlFor="locationName"
            isRequired
          >
            Location Name
          </Label>
          <div className="relative">
            <Input
              {...register('locationName', { required: EMPTY_FIELD_ERROR })}
              variant="tertiary"
              id="locationName"
              name="locationName"
              customClass="h-[56px]"
              placeholder="e.g., Classroom A2"
            />
            <span className="absolute right-4 top-5">
              <TooltipV2
                text="Provide the name of the physical location where this beacon is installed"
                arrowPosition="right"
              >
                <InfoIcon />
              </TooltipV2>
            </span>
          </div>
          {errors.locationName && (
            <span className="text-red-500 text-[12px]">{errors.locationName.message}</span>
          )}
        </div>
      </div>

      <div className="flex justify-between mt-[45px] gap-[32px]">
        <SecondaryButton fullWidth onClick={onClose}>
          Cancel
        </SecondaryButton>
        <PrimaryButton disabled={!isValid} fullWidth type="submit">
          Save
        </PrimaryButton>
      </div>
    </form>
  );
};

/**
 * Modal component for adding a new beacon.
 * It contains the `AddBeaconForm` component and passes necessary props to it.
 *
 * @param {Object} props - The properties passed to the component.
 * @param {string} props.title - The title of the modal (optional).
 * @param {Function} props.onSubmit - Callback function to handle form submission.
 * @param {Function} props.onClose - Callback function to handle modal close.
 * @param {Array} props.organizations - List of organizations for the dropdown.
 * @param {Boolean} props.isUserAdmin - Flag indicating if the user is an admin.
 * @param {String} props.userOrganization - The user's organization if not an admin.
 * @param {Object} props.initialData - Initial data for pre-filling the form (optional).
 * @returns {JSX.Element} - The rendered modal component containing the form.
 */
const BeaconModal = ({
  title = 'Add Beacon',
  onSubmit = () => {},
  onClose = () => {},
  organizations,
  isUserAdmin,
  userOrganization,
  initialData,
}) => {
  const handleSubmit = (data) => {
    onSubmit(data);
  };

  return (
    <BaseModalV2 onClose={onClose} title={title}>
      <BeaconForm
        onSubmit={handleSubmit}
        onClose={onClose}
        organizations={organizations}
        isUserAdmin={isUserAdmin}
        userOrganization={userOrganization}
        initialData={initialData}
      />
    </BaseModalV2>
  );
};

export default BeaconModal;
